﻿import { ClientEnvInfo } from './ClientEnvInfo.js';
import { StringUtils } from './ObjectUtils.js';

let ErrorHandler = null;

/* A note about exception stacks: if you are using your own exception classes, by subclassing the
 * Error object, e.g. "class MyException extends Error", you gain the benefit of adding a stack
 * trace to the exception object (see the stack property). */
export class GlobalErrorHandler {
  constructor(adminService) {
    this.adminService = adminService;
    // Install global error handler:
    window.addEventListener('error', event => this.handleErrorEvent(event));
    // This one is handling rejected promises (exceptions inside promises and await calls):
    window.addEventListener("unhandledrejection", event => this.handleErrorEvent(event));
    ErrorHandler = ErrorHandler || this;
    this.envInfo = new ClientEnvInfo();
  }

  isExcludedErrorEvent(event) {
    const href = window.location.href;
    if (href.indexOf('fbclid=') > -1 && href.indexof(':face:') > -1) {
      /* This is a request from a Facebook server. It seems the server environment changes
       * important aspects of the JS runtime environment causing exceptions to be thrown.
       * https://sagapixel.com/facebook-advertising/what-is-fbclid/
       * https://github.com/aFarkas/lazysizes/issues/520
       */
      return true;
    } else if (typeof event.reason === 'string' && event.reason.includes('Object Not Found Matching Id')) {
      /* This seems to come from a .NET application with an embedded Chromium instance using CEFSharp. See here for more:
       * https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/19 */
      return true;
    }
    let message;
    if (event.message) {
      message = event.message;
    } else if (event.reason && event.reason.message) {
      message = event.reason.message;
    }
    if (message) {
      if (
        message.includes('jQuery is not defined') ||
        message.includes('$(...).tooltip is not a function') ||
        message.includes('$ is not defined') ||
        message.includes('vendorDelayedWaiter error notification: Unable to load script.') ||
        message.includes('BABYLON is not defined')
      ) {
        // Symptom of old incompatible browser, script not loaded...
        return true;
      } else if (message.includes(`Can't find variable: gmo`)) {
        /* This is a recent issue with the Google Search app on iOS. See here for more information:
         * https://issuetracker.google.com/issues/396043331. 2/14/2025. */
        return true;
      }
    }
    if (event.error && event.error.stack && event.error.stack.includes('chrome-extension')) {
      // We don't care about error originating in Chrome extensions
      return true;
    }
    return false;
  }

  addErrorInfo(error, errorInfo) {
    const exceptionMessage = errorInfo.ExceptionMessage || errorInfo.exceptionMessage || errorInfo.message;
    if (exceptionMessage) {
      error.message += '. ' + exceptionMessage;
      error.stack = errorInfo.StackTrace || errorInfo.stackTrace || errorInfo.stack || '(See server side error report, i.e. email)';
    }
    const exceptionType = errorInfo.ExceptionType || errorInfo.exceptionType;
    if (exceptionType) {
      error.message += '\r\nException type: ' + errorInfo.exceptionType;
    } else if (StringUtils.isJson(errorInfo)) {
      error.message += '\r\nError info (json): ' + errorInfo;
    }
  }

  /**
   * We're creating an error object for a failed fetch promise inside one of our HttpService objects.
   * This is typically a 500 error on the server communicated back to us.
   * @param {any} reason The event.reason object, typically the Error object
   * @param {any} ts Our timestamp for the error
   */
  createHttpServiceError(reason, ts) {
    const error = {
      message: reason.message,
      errObjType: typeof reason,
      time: ts.toUTCString(),
      source: reason.source,
      stack: null,
    };
    if (reason.errorInfo) {
      this.addErrorInfo(error, reason.errorInfo);
    }
    if (reason.stack) {
      error.stack += '\r\n\r\n----------------------\r\n\r\n' + reason.stack;
    }
    if (reason.response) {
      if (reason.response.url) {
        error.source += ', URL: ' + reason.response.url;
      }
      if (reason.response.status) {
        error.message += '\r\nResponse status: ' + reason.response.status;
      }
    }
    return error;
  }

  /**
   * We're creating an error object for a rejected promise. This typically happens when the promise
   * is rejected by calling AbortController.abort().
   * @param {any} event A PromiseRejectionEvent object
   * @param {any} ts Our timestamp for the error
   */
  createPromiseRejectionError(event, ts) {
    const reason = event.reason;
    if (typeof reason !== 'object') {
      return {
        message: reason,
        errObjType: typeof reason,
        time: ts.toUTCString(),
        source: `${event.constructor.name} of type ${event.type}`,
        stack: null,
      };
    } else {
      const error = {
        message: reason.message,
        errObjType: `${reason.constructor.name} (${reason.name})`,
        time: ts.toUTCString(),
        source: `${event.constructor.name} of type ${event.type}`,
        stack: reason.stack, // ...is normally not assigned
      };
      if (reason.code) {
        error.message += ` (code: ${reason.code})`;
      }
      return error;
    }
  }

  async handleErrorEvent(event) {
    let error = null;
    try {
      if (!event || !this.adminService || this.isExcludedErrorEvent(event)) return;

      const ts = new Date();

      if (event.promise && event.reason) {
        // We're handling an "unhandledrejection" event (rejected promise).
        const reason = event.reason;
        if (reason.source == 'HttpService') {
          /* We're handling a failed fetch promise inside one of our HttpService objects.
           * This is typically a 500 error on the server communicated back to us. */
          error = this.createHttpServiceError(reason, ts);
        } else if (event instanceof PromiseRejectionEvent) {
          error = this.createPromiseRejectionError(event, ts);
        } else {
          event.error = reason;
          event.filename = '(Promise)'; // We don't have any filename or source info, just know that the error happened inside a promise
        }
      }
      if (error) {
        // This is an internal signal that we have already fully populated the error object.
      } else {
        error = {
          message: event.message,
          errObjType: typeof event.error,
          time: ts.toUTCString(),
          source: event.filename,
          lineNo: event.lineno,
          colNo: event.colno,
          stack: null,
        };
        if (event.error) {
          if (typeof event.error == 'string') {
            error.message = event.error;
          } else if (event.error.errorInfo) {
            this.addErrorInfo(error, event.error.errorInfo);
          } else if (typeof event.error == 'object') {
            error.message = event.error.message || event.message;
          }
          if (event.error.stack) {
            error.stack = event.error.stack + '\n\n' + error.stack;
          }
          if (event.error.constructor) {
            error.errObjType = event.error.constructor.name || error.errObjType;
          }
        }
      }

      this.envInfo.refresh();
      error.url = window.location.href;
      error.referrer = document.referrer;
      error.browser = this.envInfo.device.browser;
      error.opSys = this.envInfo.device.opSysAndVersion;
      error.innerSize = `${window.innerWidth} x ${window.innerHeight} px`;
      // We can do this too:
      //error.props = [
      //  { name: 'User Agent', value: this.envInfo.device.userAgent }  // If we want to see the user agent in the error report.
      //];

      if (error.message === 'Script error.') {
        //error.message += ' (Underlying message is most likely hidden due to the browser\'s interpretation of CORS security risk.)'
        /* 5/10/2021: I'm unable to trace the source of these errors, most likely it's one of the third
         * party scripts, e.g. Facebook, GA, CookieConsent. Let's ignore these errors for the time being. */
        return;
      }

      if (!window.isModuleScriptsSupported) {
        error.message +=
          '\r\nAdditional info:' +
          '\r\n* Are module scripts supported: NO' +
          `\r\n* Are ES5 scripts added: ${window.isEs5ScriptsAdded ? 'YES' : 'NO'}` +
          `\r\n* Are ES5 scripts loaded: ${window.isEs5ScriptsLoaded ? 'YES' : 'NO'}`;
      }

      if (this.equalsPriorError(error, ts)) {
        // Don't submit error, but increase repetition counter and update timestamp
        this.priorError.count++;
        this.priorError.ts = ts;
      } else {
        if (this.hasRepeatedPriorError()) {
          await this.submitPriorErrorSummary();
        }
        this.saveAsPriorError(error, ts);
        await this.adminService.submitError(error);
      }
    } catch (ex) {
      // Ignore errors inside the error handler to avoid a never ending loop of reporting errors
      console.error(
        'Exception thrown inside the GlobalErrorHandler. The original error has probably not been reported to the server:\r\n',
        ex, '\r\nHere is the original error:\r\n', error);
    }
  }

  saveAsPriorError(error, ts) {
    error.ts = ts;
    error.count = 1;
    this.priorError = error;
  }

  equalsPriorError(error, ts) {
    return this.priorError &&
      error.url === this.priorError.url &&
      error.message === this.priorError.message &&
      error.source === this.priorError.source &&
      ts - this.priorError.ts < 10000;
  }

  hasRepeatedPriorError() {
    return this.priorError && this.priorError.count > 1;
  }

  async submitPriorErrorSummary() {
    if (this.priorError) {
      this.priorError.props = this.priorError.props || [];
      this.priorError.props.push({ name: 'Error Repeat Count', value: this.priorError.count });
      await this.adminService.submitError(this.priorError);
    }
  }

  /**
   * Method which can be called directly from user code when an exception is caught,
   * but should be reported (i.e. in a catch clause where you're not rethrowing the exception).
   * Requires access to a GlobalErrorHandler instance.
   */
  async handleException(ex) {
    const event = {
      message: ex.message,
      error: ex,
    };
    await this.handleErrorEvent(event);
  }

  /**
   * Static method which can be called directly from user code when an exception is caught,
   * but should be reported (i.e. in a catch clause where you're not rethrowing the exception).
   */
  static async HandleException(ex) {
    if (ErrorHandler) {
      await ErrorHandler.handleException(ex);
    }
  }
}

